import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useWebSocket } from './WebSocketContext';
import { notificationApi } from '../services/api';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const { socket } = useWebSocket();
  
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load notifications when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      loadNotifications();
      loadUnreadCount();
    } else {
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [isAuthenticated, user]);

  // Listen for real-time notifications
  useEffect(() => {
    if (!socket) return;

    const handleNewNotification = (notification) => {
      // Transform the real-time notification to match our structure
      const transformedNotification = {
        id: notification.id,
        title: notification.title,
        message: notification.message,
        type: notification.type,
        priority: notification.priority,
        category: notification.category,
        metadata: notification.metadata,
        createdAt: notification.createdAt,
        scheduledAt: notification.scheduledAt,
        expiresAt: notification.expiresAt,
        // Default user-specific data for new notifications
        isRead: false,
        readAt: null,
        isDelivered: true,
        deliveredAt: new Date().toISOString(),
        deliveryMethod: 'web',
        deliveryStatus: 'delivered',
        userAction: 'none',
        actionAt: null
      };
      
      setNotifications(prev => [transformedNotification, ...prev]);
      setUnreadCount(prev => prev + 1);
    };

    const handleNotificationRead = (data) => {
      // Update read status if needed
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === data.notificationId 
            ? { ...notif, isRead: true, readAt: new Date().toISOString() }
            : notif
        )
      );
    };

    socket.on('notification', handleNewNotification);
    socket.on('notification-read', handleNotificationRead);

    return () => {
      socket.off('notification', handleNewNotification);
      socket.off('notification-read', handleNotificationRead);
    };
  }, [socket]);

  const loadNotifications = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await notificationApi.getUserNotifications(params);
      
      // Transform the data to flatten the notification structure
      const transformedNotifications = (response.data.notifications || []).map(userNotification => ({
        id: userNotification.notification.id,
        title: userNotification.notification.title,
        message: userNotification.notification.message,
        type: userNotification.notification.type,
        priority: userNotification.notification.priority,
        category: userNotification.notification.category,
        metadata: userNotification.notification.metadata,
        createdAt: userNotification.notification.createdAt,
        scheduledAt: userNotification.notification.scheduledAt,
        expiresAt: userNotification.notification.expiresAt,
        // User-specific data
        isRead: userNotification.isRead,
        readAt: userNotification.readAt,
        isDelivered: userNotification.isDelivered,
        deliveredAt: userNotification.deliveredAt,
        deliveryMethod: userNotification.deliveryMethod,
        deliveryStatus: userNotification.deliveryStatus,
        userAction: userNotification.userAction,
        actionAt: userNotification.actionAt
      }));
      
      setNotifications(transformedNotifications);
    } catch (err) {
      console.error('Failed to load notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUnreadCount = useCallback(async () => {
    try {
      const response = await notificationApi.getUnreadCount();
      setUnreadCount(response.data.unreadCount || 0);
    } catch (err) {
      console.error('Failed to load unread count:', err);
    }
  }, []);

  const markAsRead = useCallback(async (notificationId, action = null) => {
    try {
      await notificationApi.markAsRead(notificationId, action);
      
      // Update local state
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId 
            ? { ...notif, isRead: true, readAt: new Date().toISOString() }
            : notif
        )
      );
      
      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      // Emit socket event for real-time updates
      if (socket) {
        socket.emit('notification-read', { notificationId });
      }
      
      return true;
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
      return false;
    }
  }, [socket]);

  const markAllAsRead = useCallback(async () => {
    try {
      await notificationApi.markAllAsRead();
      
      // Update local state
      setNotifications(prev => 
        prev.map(notif => ({ 
          ...notif, 
          isRead: true, 
          readAt: new Date().toISOString() 
        }))
      );
      
      setUnreadCount(0);
      return true;
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
      return false;
    }
  }, []);

  const registerPushToken = useCallback(async (tokenData) => {
    try {
      const response = await notificationApi.registerPushToken(tokenData);
      return response.data;
    } catch (err) {
      console.error('Failed to register push token:', err);
      throw err;
    }
  }, []);

  const unregisterPushToken = useCallback(async (token) => {
    try {
      await notificationApi.unregisterPushToken(token);
      return true;
    } catch (err) {
      console.error('Failed to unregister push token:', err);
      return false;
    }
  }, []);

  const refreshNotifications = useCallback(() => {
    loadNotifications();
    loadUnreadCount();
  }, [loadNotifications, loadUnreadCount]);

  const value = {
    notifications,
    unreadCount,
    loading,
    error,
    loadNotifications,
    loadUnreadCount,
    markAsRead,
    markAllAsRead,
    registerPushToken,
    unregisterPushToken,
    refreshNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
